import usePrivateAxios from "@/common/api/usePrivateAxios";
import { ErrorResponse } from "@/common/types/errorResponse";
import { toIsoFromDateTimeParts } from "@/common/utils/dateUtils";
import { unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { useMutation } from "@tanstack/react-query";
import z, { ZodTypeAny } from "zod";

interface UseCreateMaintenanceForwardingProps {
  workflowId?: string;
  directiveEntryType?: string;
  schema: ZodTypeAny;
  onSuccess: () => void,
  onError: (error: ErrorResponse) => void;
}

export const useCreateMaintenanceForwarding = ({
  workflowId,
  directiveEntryType,
  schema,
  onSuccess,
  onError,
}: UseCreateMaintenanceForwardingProps) => {
  const privateAxios = usePrivateAxios();

  type DynamicSchemaValidationSchema = z.infer<typeof schema>;

  return useMutation({
    mutationKey: ['createMaintenanceForwarding', workflowId, directiveEntryType],
    mutationFn: (form: DynamicSchemaValidationSchema) => {
      const payload = {
        workflow_id: workflowId,

        directive_entry_type: directiveEntryType,
        directive_entry_attributes: {
          complexity_id: form.complexity?.value,
          diagnostic_id: form.diagnostic?.value,
        },

        establishment_choice_entry_type: form.establishment_choice_entry_type,
        establishment_choice_entry_attributes: {
          establishment_id: form.establishment_choice_entry_attributes?.establishment_id,
          allowance_radius_in_kilometers: form.establishment_choice_entry_attributes?.allowance_radius_in_kilometers
            ? unFormatUnknownUnit(form.establishment_choice_entry_attributes?.allowance_radius_in_kilometers)
            : undefined,
          minimum_of_proposals_to_finish: form.establishment_choice_entry_attributes?.minimum_of_proposals_to_finish
            ? unFormatUnknownUnit(form.establishment_choice_entry_attributes?.minimum_of_proposals_to_finish)
            : undefined,
          finishes_at: form.establishment_choice_entry_attributes?.finishes_at_date
            ? toIsoFromDateTimeParts(
              form.establishment_choice_entry_attributes?.finishes_at_date,
              form.establishment_choice_entry_attributes?.finishes_at_time
            )
            : undefined,
        },

        checkin_entry_type: form.checkin_entry_type,
        checkin_entry_attributes: {
          responsible_id: form.checkin_entry_attributes?.responsible?.value,
        },
      };

      return privateAxios.post('/maintenance/forwardings', {
        forwarding: payload,
      });
    },
    onSuccess,
    onError,
  });
};
